<template>
  <div class="page-wrapper">
    <div class="profile-card">
      <p class="card-desc">修改您的资料、头像与登录密码</p>

      <el-form v-loading="loading"
        :label-position="isMobile ? 'top' : 'left'"
        :label-width="isMobile ? 'auto' : '100px'"
        class="profile-form" @submit.prevent>
        <el-form-item label="头像">
          <div class="avatar-row">
            <div class="avatar-preview">
              <img v-if="form.avatar" :src="avatarDisplayUrl" alt="" />
              <span v-else>{{ (form.name || form.username || '教').charAt(0) }}</span>
            </div>
            <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/png,image/jpeg,image/gif,image/webp"
              :on-change="onAvatarChange"
            >
              <el-button type="primary" plain size="small">更换头像</el-button>
            </el-upload>
            <el-button v-if="form.avatar" type="danger" plain size="small" @click="form.avatar = ''">清除</el-button>
          </div>
          <p class="hint">建议小于 500KB，保存后生效</p>
        </el-form-item>

        <el-form-item label="用户名">
          <el-input v-model="form.username" placeholder="登录用户名" maxlength="32" clearable />
        </el-form-item>

        <el-form-item label="姓名">
          <el-input v-model="form.name" placeholder="姓名" maxlength="50" clearable />
        </el-form-item>

        <el-form-item label="手机号">
          <el-input v-model="form.phone" placeholder="手机号" maxlength="20" clearable />
        </el-form-item>

        <el-form-item label="职称">
          <el-input v-model="form.title" placeholder="如：高级健身教练" maxlength="50" clearable />
        </el-form-item>

        <el-form-item label="专长">
          <el-input v-model="form.specialty" placeholder="逗号分隔" maxlength="200" clearable />
        </el-form-item>

        <el-form-item label="资质证书">
          <el-input v-model="form.certificate" placeholder="资质说明" maxlength="200" clearable />
        </el-form-item>

        <el-form-item label="从业年限">
          <el-input-number v-model="form.experience" :min="0" :max="50" />
        </el-form-item>

        <el-divider content-position="left">修改密码（不修改请留空）</el-divider>

        <el-form-item label="原密码">
          <el-input v-model="form.oldPassword" type="password" show-password placeholder="修改密码时必填" autocomplete="off" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="form.newPassword" type="password" show-password placeholder="至少 6 位" autocomplete="new-password" />
        </el-form-item>

        <el-form-item class="save-item">
          <el-button type="primary" :loading="saving" class="save-btn" @click="save">保存</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="profile-card reviews-card">
      <div class="card-title-row">
        <p class="card-desc" style="margin:0">学员评价</p>
        <span class="review-count">{{ reviews.length }} 条</span>
      </div>
      <div v-if="reviewsLoading" class="reviews-loading">加载中...</div>
      <div v-else-if="reviews.length === 0" class="reviews-empty">暂无学员评价，完成课程后会员可提交评价</div>
      <div v-else class="review-list">
        <div class="review-item" v-for="r in reviews" :key="r.reviewId">
          <div class="review-item-head">
            <span class="review-member">{{ r.memberName }}</span>
            <span class="review-course">{{ r.courseName }}</span>
          </div>
          <p v-if="r.appointmentTime" class="review-meta">{{ r.appointmentTime }}</p>
          <div class="review-scores">
            <span>课程 {{ r.courseRating }}/10</span>
            <span>教练 {{ r.coachRating }}/10</span>
          </div>
          <p v-if="r.courseComment" class="review-text"><strong>课程：</strong>{{ r.courseComment }}</p>
          <p v-if="r.coachComment" class="review-text"><strong>教练：</strong>{{ r.coachComment }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import api from '@/api/request'
import { resolveMediaUrl } from '@/utils/media'

const MAX_AVATAR = 500 * 1024

export default {
  name: 'CoachProfileView',
  data() {
    return {
      loading: false,
      saving: false,
      windowWidth: window.innerWidth,
      form: {
        username: '',
        name: '',
        phone: '',
        title: '',
        specialty: '',
        certificate: '',
        experience: 1,
        avatar: '',
        oldPassword: '',
        newPassword: '',
      },
      reviews: [],
      reviewsLoading: false,
    }
  },
  computed: {
    avatarDisplayUrl() {
      return resolveMediaUrl(this.form.avatar)
    },
    isMobile() {
      return this.windowWidth <= 600
    },
  },
  mounted() {
    this.load()
    this.loadReviews()
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
    async loadReviews() {
      this.reviewsLoading = true
      try {
        const res = await api.getCoachReviewList()
        if (res.data?.code === 200) {
          this.reviews = res.data.data || []
        }
      } catch {
        /* ignore */
      } finally {
        this.reviewsLoading = false
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await api.getCoachProfileMe()
        if (res.data?.code === 200 && res.data.data) {
          const d = res.data.data
          this.form.username = d.username || ''
          this.form.name = d.name || ''
          this.form.phone = d.phone || ''
          this.form.title = d.title || ''
          this.form.specialty = d.specialty || ''
          this.form.certificate = d.certificate || ''
          this.form.experience = d.experience != null ? d.experience : 1
          this.form.avatar = d.avatar || ''
        } else {
          ElMessage.error(res.data?.message || '加载失败')
        }
      } catch {
        ElMessage.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async onAvatarChange(file) {
      const raw = file.raw
      if (!raw) return
      if (raw.size > MAX_AVATAR) {
        ElMessage.warning('头像图片不能超过 500KB')
        return
      }
      try {
        const res = await api.uploadProfileImage(raw, 'coach')
        if (res.data?.code === 200 && res.data.data?.path) {
          this.form.avatar = res.data.data.path
          ElMessage.success('头像已上传，请点击保存')
        } else {
          ElMessage.error(res.data?.message || '上传失败')
        }
      } catch {
        ElMessage.error('上传失败')
      }
    },
    async save() {
      if (!this.form.username?.trim()) {
        ElMessage.warning('用户名不能为空')
        return
      }
      const payload = {
        username: this.form.username.trim(),
        name: (this.form.name || '').trim(),
        phone: (this.form.phone || '').trim(),
        title: (this.form.title || '').trim(),
        specialty: (this.form.specialty || '').trim(),
        certificate: (this.form.certificate || '').trim(),
        experience: this.form.experience,
        avatar: this.form.avatar || '',
      }
      if (this.form.newPassword) {
        payload.oldPassword = this.form.oldPassword
        payload.newPassword = this.form.newPassword
      }
      this.saving = true
      try {
        const res = await api.updateCoachProfileMe(payload)
        let body = res.data
        if (body == null || typeof body !== 'object') {
          const again = await api.getCoachProfileMe()
          body = again.data
        }
        if (body?.code === 200) {
          ElMessage.success('保存成功')
          const d = body.data || {}
          this.form.oldPassword = ''
          this.form.newPassword = ''
          this.form.username = d.username ?? this.form.username
          this.form.name = d.name ?? this.form.name
          this.form.phone = d.phone ?? this.form.phone
          this.form.title = d.title ?? this.form.title
          this.form.specialty = d.specialty ?? this.form.specialty
          this.form.certificate = d.certificate ?? this.form.certificate
          this.form.experience = d.experience != null ? d.experience : this.form.experience
          this.form.avatar = d.avatar ?? this.form.avatar
          this.$store.commit('UPDATE_USER_FIELDS', {
            username: d.username || this.form.username,
            avatar: d.avatar || '',
          })
        } else {
          ElMessage.error(body?.message || '保存失败')
        }
      } catch {
        ElMessage.error('保存失败')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page-wrapper {
  padding: 0;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.profile-card {
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 28px 24px;
}
.reviews-card .card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.review-count {
  font-size: 13px;
  color: var(--text-muted);
}
.reviews-empty, .reviews-loading {
  font-size: 14px;
  color: var(--text-muted);
  padding: 12px 0;
}
.review-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.review-item {
  padding: 14px;
  background: var(--bg-gray);
  border-radius: var(--radius-md);
}
.review-item-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.review-member {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}
.review-course {
  font-size: 13px;
  color: var(--primary-blue);
}
.review-meta {
  font-size: 12px;
  color: var(--text-muted);
  margin: 6px 0 8px;
}
.review-scores {
  display: flex;
  gap: 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
}
.review-text {
  font-size: 13px;
  color: var(--text-primary);
  margin: 4px 0 0;
  line-height: 1.5;
}
.card-desc {
  margin: 0 0 24px;
  font-size: 14px;
  color: var(--text-muted);
}
.avatar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.avatar-preview {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gradient-primary, linear-gradient(135deg, #0062ff, #3d8bff));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
}
.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hint {
  margin: 6px 6px 0;
  font-size: 12px;
  color: var(--text-muted);
}
.save-item {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .profile-card {
    padding: 18px 16px;
    border-radius: var(--radius-md);
  }
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 6px;
  }
  :deep(.el-form-item) {
    margin-bottom: 16px;
  }
  /* el-input-number 在小屏全宽 */
  :deep(.el-input-number) {
    width: 100%;
  }
  .save-btn {
    width: 100%;
  }
  .save-item :deep(.el-form-item__content) {
    display: block;
  }
}
</style>
